import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {PasteCleanupSettingsModel, RichTextEditorAllModule, ToolbarSettingsModel} from "@syncfusion/ej2-angular-richtexteditor";
import {PasteCleanupArgs} from '@syncfusion/ej2-richtexteditor';
import {LanguageService} from "../../services/language.service";
import {RichTextLinkPolicy, RichTextPastePolicy, RichTextSanitizerService} from '../../services/rich-text-sanitizer.service';

@Component({
    selector: 'app-form-textarea',
    imports: [
        NgClass,
        ReactiveFormsModule,
        RichTextEditorAllModule
    ],
    templateUrl: './form-textarea.component.html',
    styleUrl: './form-textarea.component.scss',
})
export class FormTextareaComponent implements OnInit, OnChanges, OnDestroy {
  private languageService: LanguageService = inject(LanguageService);
  private richTextSanitizer: RichTextSanitizerService = inject(RichTextSanitizerService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('textAreaElement') public textAreaElement: any;

  @Input() public placeholder: string = '';
  @Input() public fCN: string = '';
  @Input() public class: string = '';
  @Input() public fG: FormGroup = new FormGroup({});
  @Input() public errorText: string = '';
  @Input() public style: 'inline' | 'block' | 'headless' | 'chat' = 'headless';
  @Input() public disabled: boolean = false;
  @Input() public showCharCounter: boolean = true;
  @Input() public rows: number = 3;
  @Input() public cols: number = 35;
  @Input() public maxChars: number = 0;
  @Input() public height: number = 200;
  @Input() public richText: boolean = false;
  @Input() public allowedToolbarItems: string[] = [];
  @Input() public setEmoji: string = '';
  @Input() public pastePolicy: RichTextPastePolicy = 'balanced';
  @Input() public linkPolicy: RichTextLinkPolicy = 'strip';
  @Output() public blurred: EventEmitter<void> = new EventEmitter<void>();
  @Output() public returnKeyPress: EventEmitter<void> = new EventEmitter<void>();
  @Output() public documentsUpload: EventEmitter<void> = new EventEmitter<void>();
  @Output() public setEmojiChange: EventEmitter<string> = new EventEmitter<string>();

  public pasteCleanupSettings: PasteCleanupSettingsModel = {
    prompt: false,
    plainText: false,
    keepFormat: true,
    deniedTags: [],
    deniedAttrs: [],
    allowedStyleProps: ['text-align', 'text-decoration', 'font-weight', 'font-style']
  };
  public toolbarSettings: ToolbarSettingsModel = {} as ToolbarSettingsModel;

  public ngOnInit(): void {
    if (this.allowedToolbarItems.length > 0) {
      this.toolbarSettings.items = [];
      if (this.allowedToolbarItems.includes('EmojiPicker')) {
        this.toolbarSettings.items.push('EmojiPicker');
      }
      if (this.allowedToolbarItems.includes('Image')) {
        this.toolbarSettings.items.push({
          tooltipText: this.languageService.getTranslationByKey('RICHTEXTSELECTIMAGETOOLTIP'), undo: false, click: this.toolbarClicked.bind(this), template: '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
            + '<span class="mts-icon fs-4 text-primary">photo_camera</span></button>'
        });
      }
    } else {
      this.toolbarSettings.items = ['EmojiPicker', 'Bold', 'Italic', 'Underline', {
        tooltipText: this.languageService.getTranslationByKey('RICHTEXTTITLETOOLTIP'), undo: true, click: this.toolbarClicked.bind(this), template: '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
          + '<span class="e-btn-icon e-caption e-icons"></span></button>'
      }, '|', 'Alignments', 'OrderedList', 'UnorderedList', '|', 'ClearFormat'];
    }

    this.setDisabledState(this.disabled);
    this.normalizePlainTextControlValue();

    this.fG.controls[this.fCN].valueChanges.subscribe(value => {
      if (!this.richText) {
        const preparedValue = this.getPreparedPlainTextValue(value);
        if (preparedValue !== value) {
          this.fG.controls[this.fCN].setValue(preparedValue, {emitEvent: false});
          this.adjustHeight();
          return;
        }

        if (value === null) {
          if (this.textAreaElement?.nativeElement) {
            this.textAreaElement.nativeElement.style.height = 'auto';
          }
        } else {
          this.adjustHeight();
        }
      }

      if (value === '<p><br/></p>' || value === '<p><br></p>') {
        this.fG.controls[this.fCN].setValue('');
        return;
      }

    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(changes['disabled'].currentValue);
    }
    if (changes['setEmoji'] && changes['setEmoji'].currentValue && !this.richText) {
      if (!this.textAreaElement?.nativeElement) {
        return;
      }
      const startPos = this.textAreaElement.nativeElement.selectionStart;
      const endPos = this.textAreaElement.nativeElement.selectionEnd;
      const text = this.textAreaElement.nativeElement.value;
      this.textAreaElement.nativeElement.value = text.slice(0, startPos) + changes['setEmoji'].currentValue + text.slice(endPos);
      const newCursorPos = startPos + changes['setEmoji'].currentValue.length;
      this.textAreaElement.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
      this.textAreaElement.nativeElement.focus();
    }
  }

  private setDisabledState(disabled: boolean): void {
    const control = this.fG.controls[this.fCN];
    if (!control) return;
    if (disabled) {
      control.disable();
    } else {
      control.enable();
    }
  }

  public onReturnKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }
      if (this.style === 'chat') {
        event.preventDefault();
      }
      this.returnKeyPress.emit();
    }
  }

  public onPaste(event: ClipboardEvent): void {
    if (this.richText) {
      this.handleRichTextPaste();
      return;
    }

    this.handlePlainTextPaste(event);
  }

  public onBeforePasteCleanup(args: PasteCleanupArgs): void {
    if (!this.richText) {
      return;
    }

    const rawHtml = args.value ?? '';
    if (!rawHtml) {
      return;
    }

    if (this.pastePolicy === 'plain') {
      let plainValue = this.richTextSanitizer.toPlainText(rawHtml);
      if (this.maxChars) {
        const remaining = this.getRemainingTextLengthForPaste();
        plainValue = plainValue.slice(0, Math.max(0, remaining));
      }
      args.value = plainValue;
      return;
    }

    let sanitizedHtml = this.richTextSanitizer.sanitizeRichHtml(rawHtml, this.pastePolicy, this.linkPolicy);
    if (this.maxChars) {
      const remaining = this.getRemainingTextLengthForPaste();
      if (remaining <= 0) {
        args.value = '';
        return;
      }
      const sanitizedTextLength = this.richTextSanitizer.toPlainText(sanitizedHtml).length;
      if (sanitizedTextLength > remaining) {
        sanitizedHtml = this.richTextSanitizer.truncateHtmlByTextLength(sanitizedHtml, remaining);
      }
    }
    args.value = sanitizedHtml;
  }

  public onRichTextClipboard(event: ClipboardEvent, isCut: boolean = false): void {
    if (!this.richText) {
      return;
    }

    const selectionHtml = this.getSelectionHtml();
    if (!selectionHtml) {
      return;
    }

    const sanitizedHtml = this.richTextSanitizer.sanitizeRichHtml(selectionHtml, this.pastePolicy, this.linkPolicy);
    const plainText = this.richTextSanitizer.toPlainText(sanitizedHtml);

    if (!event.clipboardData) {
      return;
    }

    event.preventDefault();
    event.clipboardData.setData('text/plain', plainText);
    event.clipboardData.setData('text/html', sanitizedHtml);

    if (isCut) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        selection.deleteFromDocument();
      }
    }
  }

  public toolbarClicked(): void {
    if (this.allowedToolbarItems.includes('Image')) {
      this.documentsUpload.emit();
    } else {
      this.textAreaElement!.focusIn();
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let parentNode = range.commonAncestorContainer as HTMLElement;
      if (parentNode.nodeType === Node.TEXT_NODE) {
        parentNode = parentNode.parentElement!;
      }

      if (parentNode.closest('h5')) {
        this.textAreaElement.executeCommand('formatBlock', 'P');
      } else {
        this.textAreaElement.executeCommand('formatBlock', 'H5');
      }
    }
  }

  public adjustHeight(): void {
    if (this.richText || !this.textAreaElement?.nativeElement) {
      return;
    }
    this.textAreaElement.nativeElement.style.height = 'auto';
    this.textAreaElement.nativeElement.style.height = Math.min(this.textAreaElement.nativeElement.scrollHeight, 150) + 'px';
  }

  private handleRichTextPaste(): void {
    // Keep Syncfusion native paste behavior.
    // Paste customization is handled through beforePasteCleanup.
  }

  private normalizePlainTextControlValue(): void {
    if (this.richText) {
      return;
    }

    const control = this.fG.controls[this.fCN];
    const preparedValue = this.getPreparedPlainTextValue(control?.value);
    if (preparedValue !== control?.value) {
      control.setValue(preparedValue, {emitEvent: false});
    }
  }

  private getPreparedPlainTextValue(value: unknown): unknown {
    const normalizedValue = this.getNormalizedPlainTextValue(value);
    if (this.maxChars && typeof normalizedValue === 'string' && normalizedValue.length > this.maxChars) {
      return normalizedValue.substring(0, this.maxChars);
    }

    return normalizedValue;
  }

  private getNormalizedPlainTextValue(value: unknown): unknown {
    if (this.richText || typeof value !== 'string' || !this.containsHtmlElementMarkup(value)) {
      return value;
    }

    return this.richTextSanitizer.toPlainText(value);
  }

  private containsHtmlElementMarkup(value: string): boolean {
    return /<\s*\/?\s*[a-z][a-z0-9-]*(?:\s+[^<>]*)?\/?\s*>/i.test(value);
  }

  private getRemainingTextLengthForPaste(): number {
    if (!this.maxChars) {
      return Number.MAX_SAFE_INTEGER;
    }
    const currentValue = this.fG.controls[this.fCN]?.value ?? '';
    const currentLength = this.getCurrentTextLength(currentValue);
    const selectedLength = this.getSelectedTextLength();
    return this.maxChars - (currentLength - selectedLength);
  }

  private handlePlainTextPaste(event: ClipboardEvent): void {
    const clipboardHtml = event.clipboardData?.getData('text/html') ?? '';
    const clipboardText = event.clipboardData?.getData('text/plain') ?? event.clipboardData?.getData('text') ?? '';
    const textFromHtml = clipboardHtml ? this.richTextSanitizer.toPlainText(clipboardHtml) : '';
    const finalText = textFromHtml || clipboardText;

    if (!this.maxChars) {
      if (textFromHtml && this.textAreaElement?.nativeElement) {
        event.preventDefault();
        this.insertPlainTextIntoTextarea(textFromHtml);
      }
      return;
    }

    if (!finalText) {
      return;
    }

    const currentValue = this.fG.controls[this.fCN]?.value ?? '';
    const currentLength = this.getCurrentTextLength(currentValue);
    const selectedLength = this.getSelectedTextLength();
    const remainingLength = this.maxChars - (currentLength - selectedLength);

    if (remainingLength <= 0) {
      event.preventDefault();
      return;
    }

    if (finalText.length <= remainingLength && !textFromHtml) {
      return;
    }

    event.preventDefault();
    const trimmedText = finalText.slice(0, remainingLength);
    this.insertPlainTextIntoTextarea(trimmedText);
  }

  private insertPlainTextIntoTextarea(textToInsert: string): void {
    if (!this.textAreaElement?.nativeElement) {
      return;
    }

    const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
    const startPos = nativeElement.selectionStart ?? nativeElement.value.length;
    const endPos = nativeElement.selectionEnd ?? nativeElement.value.length;
    const existingText = nativeElement.value;
    const updatedText = existingText.slice(0, startPos) + textToInsert + existingText.slice(endPos);

    nativeElement.value = updatedText;
    const newCursorPos = startPos + textToInsert.length;
    nativeElement.setSelectionRange(newCursorPos, newCursorPos);
    this.fG.controls[this.fCN].setValue(updatedText);
  }

  private getSelectionHtml(): string {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return '';
    }

    const range = selection.getRangeAt(0);
    const container = document.createElement('div');
    container.appendChild(range.cloneContents());
    return container.innerHTML;
  }

  private getCurrentTextLength(value: string | null): number {
    if (!value) {
      return 0;
    }

    if (!this.richText) {
      return value.length;
    }

    return this.richTextSanitizer.toPlainText(value).length;
  }

  private getSelectedTextLength(): number {
    if (!this.richText && this.textAreaElement?.nativeElement) {
      const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
      return Math.max(0, (nativeElement.selectionEnd ?? 0) - (nativeElement.selectionStart ?? 0));
    }

    const selection = document.getSelection();
    return selection ? selection.toString().length : 0;
  }

  public ngOnDestroy(): void {
    if (this.richText && this.textAreaElement) {
      try {
        this.textAreaElement.destroy();
      } catch (error) {
        console.error(error);
      }
    }
  }
}
